import { Component, DestroyRef, Inject, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { MAT_DIALOG_DATA, MatDialog, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { VehiclesActions } from '@states/vehicles/vehicles.action-types';
import { Observable, take } from 'rxjs';
import { SharedSelectors } from '@states/shared/shared.selector-types';
import { PreloaderColor } from '@enums/shared.enum';
import { Upload } from '../../../../../../shared/ui-kit/ui-upload/ui-upload.models';
import { UiUploadService } from '../../../../../../shared/ui-kit/ui-upload/ui-upload.service';
import { VehiclesEffect } from '@effects/vehicles.effect';
import { ofType } from '@ngrx/effects';
import { ActiveOrganization } from '@models/organization.model';
import * as OrganizationSelectors from '@states/organization/organization.selectors';
import { VehicleModels } from '@models/vehicle.model';
import { TranslatePipe } from '@ngx-translate/core';
import { NgForOf, NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { DirectivesModule } from '../../../../../../shared/directives/directives.module';
import { UiKitModule } from '../../../../../../shared/ui-kit/ui-kit.module';
import { EnableEscCloseDirective } from '../../../../../../shared/directives/enable-esc-close.directive';
import { FileProcessorService } from '../../../../../../services/file-processor-service.service';
import { FileUploadStatusComponent } from '../../../../../../shared/components/file-upload-status/file-upload-status.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FailedFile, ValidFile } from '../../../../../../services/confirm-dialogs/modals/file-processor.models';
import { UiButtonComponent } from '@ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface VehiclesUploadDialogData {
  isSearch: boolean;
  multiple?: boolean;
}

@Component({
  selector: 'app-vehicles-upload-dialog',
  templateUrl: './vehicles-upload-dialog.component.html',
  styleUrls: ['./vehicles-upload-dialog.component.scss'],
  imports: [
    TranslatePipe,
    MatDialogClose,
    MatIcon,
    NgIf,
    DirectivesModule,
    UiKitModule,
    NgForOf,
    UiButtonComponent,
    FileUploadStatusComponent,
    MatProgressSpinner
  ],
  hostDirectives: [EnableEscCloseDirective],
})
export class VehiclesUploadDialogComponent implements OnInit, OnDestroy {
  public selectedFiles: File[] = [];
  public name: string;

  public selectActiveOrganization$: Observable<ActiveOrganization> = this.store$.pipe(
    select(OrganizationSelectors.selectActiveOrganization),
  );

  public PreloaderColor = PreloaderColor;

  public selected: boolean = false;
  public progress: number = 0;
  public uploadDone: boolean = false;

  public uploadProgress$: Observable<Upload>;

  public evaluateResponse: VehicleModels.EvalCsvResponse;
  public evaluateResponseMultiple: string[];

  public matchingIdx: number = 0;
  public orgId: string;
  public loading: boolean = false;
  public label: string = 'Uploading...';
  public bestImageUrl: string = '';
  public errorMsg: string;

  public failedFiles: FailedFile[] = [];
  public validFiles: ValidFile[] = [];

  public selectIsSaving$: Observable<boolean> = this.store$.pipe(select(SharedSelectors.selectIsSaving));

  constructor(
    private store$: Store,
    private dialog: MatDialog,
    private vehiclesEffect: VehiclesEffect,
    private dialogRef: MatDialogRef<VehiclesUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: VehiclesUploadDialogData,
    private uiUploadService: UiUploadService,
    private fileProcessorService: FileProcessorService,
    private destroyRef: DestroyRef,
  ) { }

  public ngOnDestroy(): void {
    this.uiUploadService.clearProgress();
  }

  public ngOnInit(): void {
    this.selectActiveOrganization$.pipe(take(1)).subscribe((org) => {
      this.orgId = org?.orgId;
    });
    this.uploadProgress$ = this.uiUploadService.uploadProgress$;
    this.uploadProgress$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((upload) => {
      this.progress = upload.progress;
      if (upload.state === 'DONE') {
        this.uploadDone = false;
        this.label = 'Processing...';
      }
    });

    this.vehiclesEffect.uploadCsvSendSuccess$
      .pipe(takeUntilDestroyed(this.destroyRef), ofType(VehiclesActions.UploadCsvSendSuccess))
      .subscribe((result) => {
        this.loading = false;
        this.evaluateResponse = result.response;
        this.dialogRef.close(true);
      });

    this.vehiclesEffect.uploadCsvSendError$
      .pipe(takeUntilDestroyed(this.destroyRef), ofType(VehiclesActions.UploadCsvSendError))
      .subscribe((result) => {
        this.onError();
        this.errorMsg = 'Unknown error, try again';
      });

    if (this.data?.isSearch) {
      this.name = 'Unnamed';
    }
  }

  public onError(): void {
    this.loading = false;
    this.selected = false;
    this.selectedFiles = [];
    this.uploadDone = false;
    this.progress = 0;
    this.failedFiles = [];
    this.validFiles = [];
    delete this.evaluateResponse;
    delete this.evaluateResponseMultiple;
  }

  public uploadImages(): void {
    this.dialog
      .open(VehiclesUploadDialogComponent, {
        width: '400px',
        panelClass: 'modal-no-padding',
      })
      .afterClosed()
      .subscribe((name) => { });
  }

  public onFileDrop(event: any): void {
    const files = Array.from(event instanceof FileList ? event : (event as File[]));
    this.selectedFiles = [...this.selectedFiles, ...files];
    this.uploadMulti(this.selectedFiles);
  }

  public onFileChange(event: any): void {
    const files = Array.from(event.target.files as FileList);
    this.selectedFiles = [...this.selectedFiles, ...files];

    this.uploadMulti(this.selectedFiles);

    event.target.value = '';
  }

  public async uploadMulti(file: File[]): Promise<void> {
    const assets: VehicleModels.FileAsset[] = [];
    this.selected = true;
    this.loading = true;

    const config = {
      allowedExtensions: ['csv'],
      maxSizeMb: 1,
      requiredHeaders: ["plate", "owner", "make", "color"]
    };

    const results = await this.fileProcessorService.processFiles(
      this.selectedFiles,
      config
    );

    this.validFiles = results
      .filter(r => r.success)
      .map(r => ({
        name: r.fileName,
      }));

    this.failedFiles = results
      .filter(r => !r.success)
      .map(r => ({
        name: r.fileName,
        error: r.error
      }));

    const validFileNames = results
      .filter(r => r.success)
      .map(r => r.fileName);

    const validSelectedFiles = this.selectedFiles.filter(f => validFileNames.includes(f.name));

    if (validSelectedFiles.length > 0) {
      const assets: VehicleModels.FileAsset[] = [];
      for (const file of validSelectedFiles) {
        assets.push({
          file,
          asset: { filename: `${Date.now().toString()}_${file.name}` },
        });
      }

      this.store$.dispatch(
        VehiclesActions.UploadCsv({
          request: assets,
          multiple: this.data?.multiple ?? false,
        }),
      );
    } else {
      this.loading = false;
    }
  }

  public save(): void {
    this.dialogRef.close();
  }

  public reset(): void {
    this.loading = false;
    this.selected = false;
    this.selectedFiles = [];
    this.uploadDone = false;
    this.progress = 0;
    this.failedFiles = [];
    this.validFiles = [];
    delete this.evaluateResponse;
    delete this.evaluateResponseMultiple;
  }
}
